import { useEffect, useRef, useState } from 'react';
import { hashKey, useIsFetching, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import {
  REFRESH_SIGNALS_CLIENT_TIMEOUT_MS,
  REFRESH_SIGNALS_MAX_SIGNAL_IDS,
  RefreshSignalsResponseSchema,
  type RefreshSignalsBody,
  type RefreshSignalsResponse,
} from '@shared/schemas/terra-signals';
import { invalidateSignalQueries } from './useSignalsMutations';
import { FLOW_CONTACT_TIMELINE_KEY } from '../contact-state-keys';
import { FLOW_CONTACT_INTEL_KEY } from '../contact/hooks';

/**
 * `useRefreshSignals` (bead .17, U7) — the refresh-on-read POST, wrapped in
 * a `useQuery` (not `useMutation`) so `useRefreshOnMount.ts` gets StrictMode
 * fire-once for free from TanStack's in-flight request dedupe on the shared
 * `['flow-signals-refresh']` key (see that bead's "Resolved" note and
 * `docs/v4-system/strictmode-resilient-effects.md` — a lifetime `firedRef`
 * fires TWICE under StrictMode's `setup -> cleanup -> setup`; this doesn't).
 *
 * `staleTime: 0` / `gcTime: 0` / no refetch-on-focus/-reconnect: a fresh
 * mount always fetches once, a real remount after the first settled starts
 * a new fetch (the cache entry is gone), and nothing re-fires on its own.
 *
 * The `invalidateSignalQueries` call (fix-plan-batch2.md D-D / U7 — the same
 * set `useSignalsMutations.ts` invalidates after snooze/unsnooze/archive:
 * `['flow-signals']`, the contact-signals key AND the account-signals key)
 * happens INSIDE the queryFn, right after a successful parse — not in a
 * `useEffect` watching `data`. Both `SignalsFlowTable` (via
 * `useRefreshOnMount`) and `SignalsToolbar` (this hook directly, for the
 * Refresh button) mount a `useQuery` on the SAME key at the same time; the
 * queryFn itself only runs once per actual fetch (shared across every
 * observer), so invalidating there is the one placement that can't
 * double-invalidate a single settle event the way an effect duplicated
 * across two call sites would.
 */

export const REFRESH_QUERY_KEY = ['flow-signals-refresh'] as const;
const REFRESH_QUERY_HASH = hashKey(REFRESH_QUERY_KEY);

/** The Signal card's read of the last refresh — `lastCheckFailed` per qa-batch4-plan.md D-B: an error, or
 *  a `refreshed: true` response whose Gong check was skipped (`gongCheck == null`), had failed batches or hit
 *  its deadline. Throttled / 202 → false. Reads the cache entry directly; `undefined` (no entry) → false. */
function readLastCheckFailed(queryClient: QueryClient): boolean {
  const state = queryClient.getQueryState<RefreshSignalsResponse>(REFRESH_QUERY_KEY);
  if (!state) return false;
  const data = state.data;
  return !!state.error || (!!data?.refreshed && (data.gongCheck == null || data.gongCheck.failedBatches > 0 || data.gongCheck.deadlineHit));
}

/** Whether a Gong check is in flight / how the last one ended — for the Signal card's status line
 *  (qa-batch4-plan.md D-B). PASSIVE in the strict sense (fix-plan-batch4.md D-B, Codex batch-4 F2): it
 *  attaches NO QueryObserver — a `useQuery({ enabled: false })` still builds the entry and holds it out
 *  of gc until it unmounts, which would let a mounted card keep `useRefreshSignals`'s `gcTime: 0` entry
 *  alive after the toolbar and table observers leave. Instead it does what `useIsFetching` does inside:
 *  subscribes to the QueryCache and reads `getQueryState`. So `useRefreshOnMount`'s fire-once-per-real-
 *  mount contract (its "a real remount … fires again" test) holds with a card mounted, and the reader
 *  alone never creates the entry. `useSyncExternalStore` is banned by `terra/no-hand-rolled-external-store`,
 *  hence effect + state; the effect re-reads on (re)mount before subscribing, so StrictMode's
 *  setup → cleanup → setup and a real remount both start from the cache's truth with no lifetime ref. */
export function useGongCheckActivity(): { inFlight: boolean; lastCheckFailed: boolean } {
  const queryClient = useQueryClient();
  const inFlight = useIsFetching({ queryKey: REFRESH_QUERY_KEY }) > 0;
  const [lastCheckFailed, setLastCheckFailed] = useState(() => readLastCheckFailed(queryClient));
  useEffect(() => {
    setLastCheckFailed(readLastCheckFailed(queryClient));
    return queryClient.getQueryCache().subscribe(event => {
      if (event.query.queryHash !== REFRESH_QUERY_HASH) return;
      setLastCheckFailed(readLastCheckFailed(queryClient));
    });
  }, [queryClient]);
  return { inFlight, lastCheckFailed };
}

/**
 * B4 — the ids worth re-verifying against Gong: only Signal Action rows have
 * a Gong flow (the server's `RefreshSignalsBodySchema` rejects any other
 * prefix), and at most `REFRESH_SIGNALS_MAX_SIGNAL_IDS`, top of the list
 * first. `null` when none qualify, so the body stays `{}`.
 */
function gongCheckableSignalIds(signalIds: string[] | undefined): string[] | null {
  const ids = (signalIds ?? [])
    .filter((id) => id.startsWith('signal_action:'))
    .slice(0, REFRESH_SIGNALS_MAX_SIGNAL_IDS);
  return ids.length > 0 ? ids : null;
}

export interface UseRefreshSignalsResult {
  isPending: boolean;
  lastResult: RefreshSignalsResponse | null;
  error: Error | null;
  /**
   * `signalIds` (B4): the loaded rows the server should re-verify against
   * Gong alongside the rep's own. Omitted → `{}` (own rows only).
   */
  refresh: (opts?: { signalIds?: string[] }) => void;
}

export function useRefreshSignals(): UseRefreshSignalsResult {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  // B4: the Refresh button's loaded ids for the NEXT fetch. A `useQuery`
  // refetch takes no arguments (the fetch stays a query for the StrictMode
  // dedupe above), so the button stages its ids here and the queryFn takes
  // them — consume-once, so a later mount-path fetch never re-sends a stale
  // press's ids. Not render state: nothing renders from it.
  const pendingSignalIdsRef = useRef<string[] | null>(null);

  const query = useQuery<RefreshSignalsResponse>({
    queryKey: REFRESH_QUERY_KEY,
    queryFn: async () => {
      // R20 — `apiFetch`'s 15 s default is shorter than the upstream
      // Middleman round trip this POST waits on (~45 s budget), so a
      // slow-but-SUCCESSFUL refresh was aborted client-side: the rep saw
      // an error, no refetch happened, and the durable server-side work
      // landed invisibly. `REFRESH_SIGNALS_CLIENT_TIMEOUT_MS` (60 s) covers
      // the real budget with headroom, AND is the one number the server's
      // live-check deadline (D-C) is derived from — U3 exports it so
      // client and server timing can never drift apart.
      const signalIds = pendingSignalIdsRef.current;
      pendingSignalIdsRef.current = null;
      const body: RefreshSignalsBody = signalIds ? { signalIds } : {};
      const parsed = await fetchJson(apiFetch, RefreshSignalsResponseSchema, '/api/flow/signals/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        timeoutMs: REFRESH_SIGNALS_CLIENT_TIMEOUT_MS,
      });
      // R21 — the Ranker is invalidated on ANY successfully parsed outcome,
      // including `throttled` and the 202 fail-soft: `['flow-signals-ranking']`
      // is `staleTime: Infinity` and nothing else in the tab ever
      // invalidates it, so a ranking changed elsewhere could otherwise
      // never be picked up without a full page reload.
      await queryClient.invalidateQueries({ queryKey: ['flow-signals-ranking'] });
      if (parsed.refreshed) {
        // F3 (fix-plan-batch2.md D-D) — a live check may have changed a
        // signal's verdict, so the list AND the open card's contact-/
        // account-signal reads (Timeline, selector) must all go stale
        // together, not just the list.
        await invalidateSignalQueries(queryClient);
        // Signals V3 U12 — the refresh also re-mirrors campaign member notes, so
        // the timeline (which shows them) goes stale and every cached contact
        // summary (which recaps them) is invalidated: an OPEN one regenerates
        // once, ones no longer open are dropped rather than paying for a
        // regeneration nobody is looking at. Invalidate-on-mutation, no TTL and
        // no client-side notes fingerprint.
        //
        // Cancel first: `invalidateQueries` REUSES a request already in flight
        // (an INITIAL load, no cached data yet, that started before this
        // refresh) rather than restarting it — it would finish with the
        // pre-refresh notes, clear the invalidation, and cache them forever
        // under `staleTime: Infinity`. `cancelQueries` (revert: true, the
        // default) drops that request and restores the prior state with no
        // error, so the invalidation below starts a fresh one for any active
        // observer.
        await queryClient.cancelQueries({ queryKey: FLOW_CONTACT_TIMELINE_KEY });
        await queryClient.invalidateQueries({ queryKey: FLOW_CONTACT_TIMELINE_KEY });
        queryClient.removeQueries({ queryKey: FLOW_CONTACT_INTEL_KEY, type: 'inactive' });
        await queryClient.cancelQueries({ queryKey: FLOW_CONTACT_INTEL_KEY });
        await queryClient.invalidateQueries({ queryKey: FLOW_CONTACT_INTEL_KEY });
      }
      return parsed;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Double-error-presentation fix: this error already renders inline
    // (`SignalsToolbar`'s Refresh-button tooltip, `title={error.message}`),
    // so it must opt out of the global QueryCache toast too — the shared
    // `meta.suppressErrorToast` convention read by
    // `lib/mutation-cache-handler.ts`'s `makeQueryCacheOnError`.
    meta: { suppressErrorToast: true },
  });

  return {
    isPending: query.isFetching,
    lastResult: query.data ?? null,
    error: query.error,
    /**
     * R21 — the Refresh BUTTON always re-reads Terra. Inside the server's
     * 5-minute throttle window the POST comes back `throttled: true`, which
     * (correctly) skips the queryFn's list invalidation; but the list is
     * `staleTime: Infinity`, so pressing Refresh did visibly nothing. The
     * button therefore invalidates the list itself whenever the server
     * didn't already do a refresh for it — and only then, so a `refreshed:
     * true` press doesn't load the list twice.
     *
     * The MOUNT path (`useRefreshOnMount`) deliberately does NOT go through
     * here: a throttled mount must stay quiet rather than double-load a
     * list the page is already fetching.
     */
    refresh: (opts) => {
      pendingSignalIdsRef.current = gongCheckableSignalIds(opts?.signalIds);
      void query.refetch().then(r => {
        if (r.data && !r.data.refreshed) {
          // F3′ — same set as the queryFn's `refreshed` branch above (D-D):
          // the button's own throttled reread must go stale on the list AND
          // the entity-signal keys too, not just the list.
          void invalidateSignalQueries(queryClient);
        }
      });
    },
  };
}
